import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { MembershipOrderTbbProvider, useMembershipOrderTbbContext, MEMBERSHIP_ORDER_TBB_STEPS } from './MembershipOrderTbbContext';
import MembershipOrderTbb from './MembershipOrderTbb';
import AddressesContainer from '../../global/AddressesContainer';
import { getMembershipAddresses, createUserAddress, updateUserAddress } from '../../utils/service';
import OrderConfirmation from './OrderConfirmation';
import getPriorityAddress from '../../utils/addresses';
import LoadingBar from '../../global/LoadingBar';

import './MembershipOrderTbbContainer.scss';

const MembershipOrderTbbContent = ({
  containerClassName,
  hideOrderCta,
  useWhiteCards,
  orderConfirmationShowCta,
  productSelectionClassName,
  addressesContainerClassName,
  orderConfirmationClassName,
  showOrderConfirmationPromoSection,
  onAddressValidated,
  earnedTCAmount,
  defaultStep,
  addressOnly,
  addressFormSubmitButtonText,
  newAddressFormTitle,
  designSystemForms,
  stayOnAddressFormAfterSave,
  onValidAddressChange,
  hideAddressFormBackLink,
}) => {
  const {
    isLoading,
    setIsLoading,
    error,
    setError,
    currentStep,
    selectedAddress,
    goBackToProductSelection,
    membershipAddresses,
    setMembershipAddresses,
    setSelectedAddress,
    refetchMembershipAddresses,
    setRefetchMembershipAddresses,
    isFirstOrder,
    isOneTimeOrder,
    isAnonymous,
    isSignedIn,
  } = useMembershipOrderTbbContext();

  const fetchMembershipAddresses = useCallback(async (prevSelectedAddress) => {
    setIsLoading(true);

    const { data, error: addressesError } = await getMembershipAddresses({ pageSize: 2 });

    if (addressesError) {
      setError(addressesError);
      setIsLoading(false);
      return;
    }

    const addresses = data?.userAddresses || [];
    setMembershipAddresses(addresses);

    if (addresses.length > 0) {
      const defaultAddress = prevSelectedAddress
        && addresses.find(address => address.uid === prevSelectedAddress.uid);

      setSelectedAddress(defaultAddress || getPriorityAddress(addresses) || addresses[0]);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isAnonymous && !isSignedIn) {
      setIsLoading(false);
      return;
    }

    if (currentStep && currentStep !== MEMBERSHIP_ORDER_TBB_STEPS.ORDER_CONFIRMATION) {
      fetchMembershipAddresses(selectedAddress);
    }

    if (refetchMembershipAddresses) {
      setRefetchMembershipAddresses(false);
    }
  }, [currentStep, refetchMembershipAddresses, fetchMembershipAddresses, isAnonymous, isSignedIn]);

  const handleAddressSelect = useCallback((address) => {
    setSelectedAddress(address);
  }, [setSelectedAddress]);

  const handleAddressSave = useCallback(async (addressData) => {
    if (isAnonymous && !isSignedIn) {
      setMembershipAddresses([addressData]);
      setSelectedAddress(addressData);
      return;
    }

    setIsLoading(true);

    // If the address has a UID, it's an update; otherwise, it's a new address
    if (addressData.uid) {
      const { error: addressError } = await updateUserAddress({
        addressUid: addressData.uid,
        fromAddress: addressData,
      });

      if (addressError) {
        setError(addressError);
      } else {
        setRefetchMembershipAddresses(true);
      }
    } else {
      const { error: addressError } = await createUserAddress({
        fromAddress: addressData,
      });

      if (addressError) {
        setError(addressError);
      } else {
        setRefetchMembershipAddresses(true);
      }
    }

    setIsLoading(false);
  }, [isSignedIn, isAnonymous]);

  const handleBack = useCallback(() => {
    goBackToProductSelection();
  }, []);

  const renderCurrentStep = () => {
    const stepToRender = addressOnly ? MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER : currentStep;

    switch (stepToRender) {
      case MEMBERSHIP_ORDER_TBB_STEPS.PRODUCT_SELECTION:
        return (
          <div className={classNames('MembershipOrderTbbContainer__product-selection-container', productSelectionClassName)}>
            <MembershipOrderTbb
              hideOrderCta={hideOrderCta}
              useWhiteCards={useWhiteCards}
              title={isFirstOrder
                ? 'Get Your First Order'
                : (isOneTimeOrder && 'One-Time Purchase')
                  || ''}
              onAddressValidated={onAddressValidated}
            />
          </div>
        );

      case MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER: {
        const skipProductSelection =
          addressOnly || defaultStep === MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER;
        return (
          <div className={classNames('MembershipOrderTbbContainer__address-container', addressesContainerClassName)}>
            <AddressesContainer
              addresses={membershipAddresses}
              setAddresses={setMembershipAddresses}
              selectedAddress={selectedAddress}
              onAddressSelect={handleAddressSelect}
              onAddressSave={handleAddressSave}
              showContinueButton={!skipProductSelection}
              onContinue={addressOnly ? undefined : handleBack}
              continueButtonText="Continue"
              selectionEnabled
              addressFormSubmitButtonText={addressFormSubmitButtonText}
              newAddressFormTitle={newAddressFormTitle}
              designSystemForms={designSystemForms}
              stayOnAddressFormAfterSave={stayOnAddressFormAfterSave}
              onValidAddressChange={onValidAddressChange}
              hideAddressFormBackLink={hideAddressFormBackLink}
            />
          </div>
        );
      }

      case MEMBERSHIP_ORDER_TBB_STEPS.ORDER_CONFIRMATION:
        return (
          <div className={classNames('MembershipOrderTbbContainer__order-confirmation-container', orderConfirmationClassName)}>
            <OrderConfirmation
              showCta={orderConfirmationShowCta}
              showPromoSection={showOrderConfirmationPromoSection}
              earnedTCAmount={earnedTCAmount}
            />
          </div>
        );

      default:
        return (
          <div className={classNames('MembershipOrderTbbContainer__product-selection-container', productSelectionClassName)}>
            <MembershipOrderTbb
              hideOrderCta={hideOrderCta}
              useWhiteCards={useWhiteCards}
            />
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className={classNames('MembershipOrderTbbContainer--align-center', containerClassName)}>
        <LoadingBar />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classNames('MembershipOrderTbbContainer--align-center', containerClassName)}>
        <div className="MembershipOrderTbbContainer__error-text">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('MembershipOrderTbb', containerClassName)}>
      {renderCurrentStep()}
    </div>
  );
};

const MembershipOrderTbbContainer = ({
  containerClassName,
  hideOrderCta,
  useWhiteCards,
  orderConfirmationShowCta,
  productSelectionClassName,
  addressesContainerClassName,
  orderConfirmationClassName,
  defaultStep,
  defaultValues,
  defaultSelectedAddress,
  showOrderConfirmationPromoSection = false,
  onProductsChange,
  onAddressChange,
  orderType,
  onAddressValidated,
  onStepChange,
  earnedTCAmount,
  isAnonymous,
  defaultMessages,
  addressOnly = false,
  addressFormSubmitButtonText,
  newAddressFormTitle,
  designSystemForms = false,
  stayOnAddressFormAfterSave = false,
  onValidAddressChange,
  hideAddressFormBackLink = false,
}) => (
  <MembershipOrderTbbProvider
    defaultValues={defaultValues}
    defaultStep={defaultStep}
    defaultSelectedAddress={defaultSelectedAddress}
    onProductsChange={onProductsChange}
    onAddressChange={onAddressChange}
    onStepChange={onStepChange}
    orderType={orderType}
    isAnonymous={isAnonymous}
    defaultMessages={defaultMessages}
  >
    <MembershipOrderTbbContent
      containerClassName={containerClassName}
      hideOrderCta={hideOrderCta}
      useWhiteCards={useWhiteCards}
      orderConfirmationShowCta={orderConfirmationShowCta}
      productSelectionClassName={productSelectionClassName}
      addressesContainerClassName={addressesContainerClassName}
      orderConfirmationClassName={orderConfirmationClassName}
      showOrderConfirmationPromoSection={showOrderConfirmationPromoSection}
      onAddressValidated={onAddressValidated}
      earnedTCAmount={earnedTCAmount}
      defaultStep={defaultStep}
      addressOnly={addressOnly}
      addressFormSubmitButtonText={addressFormSubmitButtonText}
      newAddressFormTitle={newAddressFormTitle}
      designSystemForms={designSystemForms}
      stayOnAddressFormAfterSave={stayOnAddressFormAfterSave}
      onValidAddressChange={onValidAddressChange}
      hideAddressFormBackLink={hideAddressFormBackLink}
    />
  </MembershipOrderTbbProvider>
);

export default MembershipOrderTbbContainer;
